// @ts-nocheck

import React from "react";
import {
    makeStyles,
    Body1,
    Caption1,
    Button,
    Text,
    Card,
    CardFooter,
    CardHeader,
    CardPreview,
    RatingDisplay,
    Link,
    Badge,
    Tooltip,
    TableBody,
    TableCell,
    TableRow,
    Table,
    TableHeader,
    Accordion,
    AccordionHeader,
    AccordionItem,
    AccordionPanel,
} from "@fluentui/react-components";
import { StoreMicrosoftRegular, ArrowDownloadFilled, PersonCircleFilled, Clock20Regular, BookNumberRegular, Money20Regular, Box20Regular, PersonCircle20Filled, CheckmarkCircle20Filled, XboxControllerRegular } from "@fluentui/react-icons";
import { useTranslation } from "react-i18next";
import { CarouselDisplay } from "./CarouselDisplay";

interface DisplayItemProps {
    result: any;
}

const useStyles = makeStyles({
    card: {
        margin: "auto",
        width: "720px",
        maxWidth: "100%",
    },
    section: {
        padding: "16px",
    },
    p2: {
        padding: "2px",
    },
});

const DisplayItem: React.FC<DisplayItemProps> = ({ result }) => {
    const styles = useStyles();
    const { t } = useTranslation();
    const [featuredBadgeIcon, setFeaturedBadgeIcon] = React.useState<HTMLSpanElement | null>(null);

    if (!result) {
        return null;
    }
    const getResultValue = (key: string) => result[key];

    const installerUrl = "https://get.microsoft.com/installer/download/" + result['productId'];

    const releaseDate =
        result['releaseDateUtc']
            ? new Date(result['releaseDateUtc']).toLocaleString('zh-CN', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
                hour12: false
            }) : null;

    const lastUpdated =
        result['productType'] && result['productType'] === "BrowserAddOn" && result['lastUpdateDateUtc']
            ?
            // e.g 1733230640.9673653
            new Date(result['lastUpdateDateUtc'] * 1000).toLocaleString('zh-CN', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
                hour12: false
            })
            : result['lastUpdateDateUtc']
                ? new Date(result['lastUpdateDateUtc']).toLocaleString('zh-CN', {
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit',
                    second: '2-digit',
                    hour12: false
                })
                : t('unknown');

    const description =
        result['description']
            ? result['description'].split('\n').map((line: string, index: number) => (
                <React.Fragment key={index}>
                    {line}
                    <br />
                </React.Fragment>
            ))
            : null;

    const publisherId =
        result['publisherId']
            ? <> <span>&nbsp;</span><small>{'('}{getResultValue('publisherId')}{')'}</small> </>
            : null;

    const categoryForApps =
        result['isApplication']
            ? (result['subcategoryId']
                ? (
                    <>
                        <Link href={"https://apps.microsoft.com/collections/apps/category/" + result['categoryId']} target="_blank">
                            {result['categories'][0]}
                        </Link>
                        {' / '}
                        {result['subcategoryId']}
                    </>
                )
                : <Link href={"https://apps.microsoft.com/collections/apps/category/" + result['categoryId']} target="_blank">
                    {result['categories'][0]}
                </Link>)
            : null;

    const categoryForMovies =
        result['isMoviesOrTVs']
            ? (result['categories']
                ? result['categories'].map((category: string) => (
                    <Link href={`https://www.microsoft.com/en-us/store/movies-and-tv/browse/top-selling?type=movies&genres=${category}`} key={category} target="_blank">
                        {category}
                    </Link>
                )).reduce((prev: React.ReactNode, curr: React.ReactNode) => [prev, ' / ', curr])
                : null)
            : null;

    const isSubscriptionBadge =
        result['isSubscription']
            ? <Badge shape="rounded" color="brand">{t('subscription_badge')}</Badge>
            : null;

    const OSBadge =
        result['productType'] && result['productType'] === "OperatingSystem"
            ? <Badge shape="rounded" color="brand">{t('operating_system')}</Badge>
            : null;

    const OSTypBadge =
        result['productType'] && result['productType'] === "OperatingSystem"
            ? <Badge shape="rounded" color="brand">{result['skus'][0]['operatingSystemType']}</Badge>
            : null;

    const BrowserAddOnCategory =
        result['productType'] && result['productType'] === "BrowserAddOn"
            ? <Link href={"https://apps.microsoft.com/collections/apps/category/" + result['categoryId']} target="_blank">
                {result['categories'][0]}
            </Link>
            : null;

    const category =
        result['productType']
            ? result['productType'] === "OperatingSystem"
                ? OSBadge
                : (result['isSubscription']
                    ? isSubscriptionBadge
                    : (result['isMoviesOrTVs']
                        ? categoryForMovies
                        : (result['productType'] === "BrowserAddOn")
                            ? BrowserAddOnCategory
                            : categoryForApps))
            : null;

    const packageFamilyName =
        result['packageFamilyNames'].length > 0 && result['packageFamilyNames'][0] !== ''
            ? <Badge shape="rounded" color="informative" icon={<Box20Regular />}>{result['packageFamilyNames'][0]}</Badge>
            : null;

    const purchaseEnabled =
        result['addOnPriceRange']
            ? <Badge shape="rounded" color="informative">{t('purchase_enabled_msg')}</Badge>
            : null;

    const priceBadge =
        result['displayPrice'].length > 0
            ? <Badge shape="rounded" color="brand" icon={<Money20Regular />}>{result['displayPrice']}</Badge>
            : null;

    const approximateSize =
        (result['approximateSizeInBytes']) && (parseFloat((result['approximateSizeInBytes'] / 1024 / 1024).toFixed(2)) > 1024)
            ? ((result['approximateSizeInBytes'] / 1024 / 1024 / 1024).toFixed(2) + " GB")
            : ((result['approximateSizeInBytes'] / 1024 / 1024).toFixed(2) + " MB");

    const approximateSizeBadge =
        result['approximateSizeInBytes']
            ? <Badge shape="rounded" color="informative">{approximateSize}</Badge>
            : null;

    const carousel =
        result['screenshots']
            ? <CarouselDisplay screenshots={result['screenshots']} trailers={result['trailers'] || []} />
            : null;

    const selfHosted =
        result['isSparkProduct']
            ? <Badge shape="rounded" color="informative" icon={<PersonCircle20Filled />}><Text>{t('self_hosted_notification')}</Text></Badge>
            : null;

    const installerData =
        Array.isArray(result['installer'])
            ? result['installer']
            : [result['installer']];

    const installer =
        (result['isApplication'] || result['isGame']) && result['installer']
            ? <CardHeader
                description={
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableCell>{t('installer_type')}</TableCell>
                                <TableCell>{t('installer_id')}</TableCell>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {installerData.map((installer: any) => (
                                <TableRow key={installer['installerType']}>
                                    <TableCell>{installer['type']}</TableCell>
                                    <TableCell>{installer['id']}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                } />
            : null;

    const viewInStoreButton =
        result['productType'] && result['productType'] === "BrowserAddOn"
            ? <Button appearance="secondary" icon={<img src="https://edgestatic.azureedge.net/welcome/static/favicon.png" height={"16px"} alt="Microsoft Edge Icon" />} as="a" href={"https://microsoftedge.microsoft.com/addons/detail/" + getResultValue('crxId')} target="_blank">{t('view_in_edge_addons')}</Button>
            : result['isGame']
                ? <Button appearance="secondary" icon={<img src="https://www.xbox.com/favicon.ico" height={"16px"} alt="Xbox Icon" />} as="a" href={"https://www.xbox.com/games/store/" + getResultValue('encodedTitle') + "/" + getResultValue('productId')} target="_blank">{t('view_in_xbox')}</Button>
                : <Button appearance="secondary" icon={<img src="https://apps.microsoft.com/assets/icons/logo-16x16.png" height={"16px"} alt="Microsoft Store Icon" />} as="a" href={"ms-windows-store://pdp?productid=" + getResultValue('productId')} target="_blank">{t('view_in_ms_store')}</Button>;

    // if neither isApplication nor isGame, then return null
    const downloadButton =
        result['isApplication'] || result['isGame']
            ? <Tooltip
                content={t('download_tooltip')}
                relationship="description"
            ><Button icon={<ArrowDownloadFilled />} as="a" href={installerUrl} target="_blank"></Button></Tooltip>
            : null;

    const platformsBadge =
        result['availableDevicesNarratorText']
            ? <> <Badge shape="rounded">{getResultValue('availableDevicesNarratorText')}</Badge>{" "} </>
            : null;

    const platforms =
        result['platforms']
            ? <section className={styles.section}>
                <Card>
                    <CardHeader
                        header={
                            <Body1>
                                {t('platforms')}
                            </Body1>
                        } />
                    <CardFooter>
                        <Text>
                            {platformsBadge}{getResultValue('platforms').join(' / ')}
                        </Text>
                    </CardFooter>
                </Card></section>
            : null;

    const videoDuration =
        result['durationInSeconds']
            ? new Date(result['durationInSeconds'] * 1000).toISOString().substr(11, 8)
            : '00:00:00';

    const videoInstances =
        result['isMovie']
            ? <section className={styles.section}>
                <Card>
                    <CardHeader
                        header={
                            <Body1>
                                {t('video_instances')}
                            </Body1>
                        } />
                    <CardFooter>
                        <Text>
                            {t('video_duration')}{videoDuration}
                        </Text></CardFooter>
                    <CardFooter>
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableCell>{t('delivery_format')}</TableCell>
                                    <TableCell>{t('resolution_detail')}</TableCell>
                                    <TableCell>{t('resolution_format')}</TableCell>
                                    <TableCell>{t('size_in_gb')}</TableCell>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {result['skus'][0]['videoInstances'].map((instance: any) => {
                                    const sizeInGB = (instance['size'] / 1024 / 1024 / 1024).toFixed(2);
                                    return (
                                        <TableRow key={instance['size']}>
                                            <TableCell>{instance['deliveryFormat']}</TableCell>
                                            <TableCell>{instance['resolutionDetail']}</TableCell>
                                            <TableCell><Badge shape="rounded">{instance['resolutionFormat']}</Badge></TableCell>
                                            <TableCell><Badge shape="rounded" color="informative">{sizeInGB} GB</Badge></TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </CardFooter>
                </Card></section>
            : null;

    const productRatingDescriptors =
        result['productRatings'] && result['productRatings'].length > 0 && (result['productRatings'].some((rating: any) => rating['ratingDescriptors'])) && (result['productRatings'].some((rating: any) => rating['ratingDescriptors'].length > 0))
            ? <CardHeader
                header={
                    <Caption1>
                        {t('rating_descriptors')}
                    </Caption1>
                }
                description={
                    <Text>
                        {result['productRatings'].flatMap((rating: any) => rating['ratingDescriptors']).map((descriptor: string, index: number) => (
                            <p key={index}>{descriptor}</p>
                        ))}
                    </Text>
                } />
            : null;

    const productRatingInteractiveElements =
        result['productRatings'] && result['productRatings'].length > 0 && (result['productRatings'].some((rating: any) => rating['interactiveElements'])) && (result['productRatings'].some((rating: any) => rating['interactiveElements'].length > 0))
            ? <CardHeader
                header={
                    <Caption1>
                        {t('rating_interactive_elements')}
                    </Caption1>
                }
                description={
                    <Text>
                        {result['productRatings'].flatMap((rating: any) => rating['interactiveElements']).map((element: string, index: number) => (
                            <p key={index}>{element}</p>
                        ))}
                    </Text>
                } />
            : null

    const productRestrictedMetadataBadge =
        result['productRatings'] && result['productRatings'].length > 0 && result['productRatings'].some((rating: any) => rating['restrictMetadata'])
            ? <Badge shape="rounded" color="informative">{t('age_rating_restrictions_metadata')}</Badge>
            : null;

    const productRestrictedPurchaseBadge =
        result['productRatings'] && result['productRatings'].length > 0 && result['productRatings'].some((rating: any) => rating['restrictPurchase'])
            ? <Badge shape="rounded" color="informative">{t('age_rating_restrictions_purchase')}</Badge>
            : null;

    const productAgeRatingRestrictions =
        result['productRatings'] && result['productRatings'].length > 0 && (result['productRatings'].some((rating: any) => rating['restrictMetadata']) || result['productRatings'].some((rating: any) => rating['restrictPurchase']))
            ? <CardHeader
                header={
                    <Caption1>
                        {t('age_rating_restrictions')}
                    </Caption1>
                }
                description={
                    <Text>
                        {productRestrictedMetadataBadge}{' '}{productRestrictedPurchaseBadge}
                    </Text>
                } />
            : null;

    const productAgeRatings =
        result['productRatings'] && result['productType'] && result['productType'] !== 'BrowserAddOn' && result['productRatings'].map((rating: any) => rating['ratingSystem'])[0]
            ? <section className={styles.section}><Card>
                <CardHeader
                    header={
                        <Body1>
                            {t('age_rating')}
                        </Body1>
                    } />
                <CardHeader
                    header={
                        <Caption1>
                            {t('age_rating_system')}
                        </Caption1>
                    }
                    description={
                        <Text>
                            {result['productRatings'].map((rating: any) => rating['ratingSystem'])[0]}{' '}<Badge shape="rounded" color="informative">{'#'}{result['productRatings'].map((rating: any) => rating['ratingSystemId'])[0]}</Badge>{' '}<Button as="a" target="_blank" icon={<BookNumberRegular />} href={result['productRatings'].map((rating: any) => rating['ratingSystemUrl'])[0]}></Button>
                        </Text>
                    } />
                <CardHeader
                    header={
                        <Caption1>
                            {t('rating_age')}
                        </Caption1>
                    }
                    description={
                        <Text>
                            {result['productRatings'].map((rating: any) => rating['ratingAge'])}
                        </Text>
                    } />
                {productAgeRatingRestrictions}
                <CardHeader
                    image={
                        <img
                            src={result['productRatings'].map((rating: any) => rating['ratingValueLogoUrl'])[0]}
                            width="32px" height="32px" alt="Rating Logo"
                        />
                    }
                    header={
                        <Caption1>
                            {t('age_rating_value')}
                        </Caption1>
                    }
                    description={
                        <Text>
                            {result['productRatings'].map((rating: any) => rating['ratingValue'])[0]}{' '}<Badge shape="rounded" color="informative">{'#'}{result['productRatings'].map((rating: any) => rating['ratingId'])[0]}</Badge>
                        </Text>
                    } />
                {productRatingDescriptors}
                {productRatingInteractiveElements}
            </Card> </section>
            : null;

    const supportedLanguages =
        result['supportedLanguages'][0]
            ? <section className={styles.section}>
                <Card>
                    {/* <CardHeader
                        header={
                            
                        } /> */}
                    <CardFooter>
                        <Text>
                            <Accordion collapsible>
                                <AccordionItem value="1">
                                    <AccordionHeader><Body1>
                                        {t('supported_languages')}
                                    </Body1></AccordionHeader>
                                    <AccordionPanel>
                                        {result['supportedLanguages'].map((language: string, index: number) => (
                                            <p key={index}>{language}</p>
                                        ))}
                                    </AccordionPanel>
                                </AccordionItem>
                            </Accordion>
                        </Text>
                    </CardFooter>
                </Card>
            </section>
            : null;

    const supportUri =
        result['supportUris'] && result['supportUris'].length > 0 && result['supportUris'][0]['uri']
            ? <Button as="a" target="_blank" href={result['supportUris'][0]['uri']}>{t('support_uri')}</Button>
            : null;

    const privacyUrl =
        result['privacyUrl']
            ? <section className={styles.section}>
                <Card>
                    <CardHeader
                        header={
                            <Body1>
                                {t('related_links')}
                            </Body1>
                        } />
                    <CardFooter>
                        <Text style={{ display: 'flex', gap: '8px' }}>
                            <Button as="a" target="_blank" href={getResultValue('privacyUrl')}>{t('privacy_policy')}</Button>
                            {supportUri}
                        </Text>
                    </CardFooter>
                </Card></section>
            : null;

    const downloadableForCurrentMarket =
        ((result['isApplication'] || result['isGame']) && result['isDownloadable'])
            ? t('downloadable_for_current_market')
            : t('not_downloadable_for_current_market');

    const hiddenFromDownloadsAndUpdates =
        result['hideFromDownloadsAndUpdates']
            ? <Badge shape="rounded" color="informative">{t('hidden_from_downloads_and_updates')}</Badge>
            : null;

    const isPrivatePreviewBadge =
        result['isPrivatePreview']
            ? <Badge shape="rounded" color="informative">{t('is_private_preview_badge')}</Badge>
            : null;

    const releaseNotes =
        result['notes'] && result['notes'].length > 0 && result['notes'][0]
            ? result['notes'][0].split('\n').map((note: string, index: number) => (
                <React.Fragment key={index}>
                    {note}
                    <br />
                </React.Fragment>
            ))
            : null;

    const releaseNotesCard =
        releaseNotes
            ? <section className={styles.section}>
                <Card>
                    <CardHeader
                        header={
                            <Body1>
                                {t('release_notes')}
                            </Body1>
                        } />
                    <CardFooter>
                        <Text>
                            {releaseNotes}
                        </Text>
                    </CardFooter>
                </Card>
            </section>
            : null;

    const permissionsRequiredCard =
        result['permissionsRequired'] && result['permissionsRequired'].length > 0
            ? <section className={styles.section}>
                <Card>
                    <CardHeader
                        header={
                            <Body1>
                                {t('permissions_required')}
                            </Body1>
                        } />
                    <CardFooter>
                        <Text>
                            {result['permissionsRequired'].map((permission: string, index: number) => (
                                <p key={index}>{permission.replace(/\.$/, '').replace(/。$/, '')}</p>
                            ))}
                        </Text>
                    </CardFooter>
                </Card>
            </section>
            : null;

    const isPwaBadge =
        result['isPwa']
            ? <Tooltip
                content={t('pwa_app_tooltip')}
                relationship="description"><Badge shape="rounded" color="informative">{t('pwa_app_badge')}</Badge></Tooltip>
            : null;

    const verifiedMicrosoftProductBadge =
        result['isMicrosoftProduct']
            ? <><Tooltip
                content={t('verified_microsoft_product_tooltip')}
                relationship="description"><Badge shape="rounded" icon={<CheckmarkCircle20Filled />}>{t('verified_microsoft_product')}</Badge></Tooltip></>
            : null;

    const warningMsgs =
        result['warningMessages'] && result['warningMessages'].length > 0
            ? result['warningMessages'].map((msg: any) => (
                <section className={styles.section}>
                    <Card>
                        <CardHeader
                            header={
                                <Body1>
                                    {msg['header']}
                                </Body1>
                            } />
                        <CardFooter>
                            <Text>
                                <Button as="a" target="_blank" href={msg['target']}>{msg['text']}</Button>
                            </Text>
                        </CardFooter>
                    </Card>
                </section>
            ))
            : null;

    const releaseDateBadge =
        releaseDate
            ? <Badge shape="rounded" color="subtle" icon={<Clock20Regular />}>{t('released')}{" "}{releaseDate}</Badge>
            : null;

    const downloadabilityCard =
        ((result['isApplication'] || result['isGame']) && result['isDownloadable'])
            ? <section className={styles.section}>
                <Card>
                    <CardHeader
                        header={
                            <Body1>
                                {t('downloadability')}
                            </Body1>
                        } />
                    <CardFooter>
                        <Text>
                            {downloadableForCurrentMarket}
                            {hiddenFromDownloadsAndUpdates}
                        </Text>
                    </CardFooter>
                </Card>
            </section>
            : null;

    const edgeAddonFeaturedBadge =
        result['isBadgedAsFeatured']
            ? <Tooltip
                content={t('edge_featured_tooltip')}
                relationship="description"
                positioning={{ target: featuredBadgeIcon }}>
                <Link href="https://learn.microsoft.com/microsoft-edge/extensions-chromium/?wt.mc_id=studentamb_203301#the-featured-badge" target="_blank">
                    <Badge color="informative" icon={{
                        ref: setFeaturedBadgeIcon, children: <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                            <path d="M10.3349 1.06532L10.4114 1.21789L10.9878 2.75087C11.0331 2.87159 11.1284 2.96685 11.2491 3.01214L12.7292 3.56741C13.3787 3.81108 13.7277 4.50512 13.5546 5.16178L13.513 5.29246L12.8244 6.81529C12.7711 6.93268 12.7711 7.06739 12.8244 7.18478L13.4783 8.62399C13.7653 9.25556 13.5213 9.99311 12.9346 10.3351L12.7821 10.4115L11.2491 10.9879C11.1284 11.0332 11.0331 11.1285 10.9878 11.2492L10.4325 12.7293C10.1889 13.3788 9.49483 13.7278 8.83817 13.5547L8.70748 13.5131L7.18466 12.8245C7.06727 12.7712 6.93256 12.7712 6.81517 12.8245L5.37596 13.4785C4.74439 13.7654 4.00683 13.5215 3.66489 12.9347L3.58847 12.7822L3.01202 11.2492C2.96672 11.1285 2.87147 11.0332 2.75075 10.9879L1.27067 10.4327C0.621169 10.189 0.272146 9.49495 0.44522 8.83829L0.486862 8.70761L1.17543 7.18478C1.22877 7.06739 1.22877 6.93268 1.17543 6.81529L0.521497 5.37608C0.234531 4.74451 0.478492 4.00696 1.0652 3.66501L1.21776 3.58859L2.75075 3.01214C2.87147 2.96685 2.96672 2.87159 3.01202 2.75087L3.56729 1.27079C3.81096 0.621291 4.505 0.272268 5.16165 0.445342L5.29234 0.486984L6.81517 1.17555C6.93256 1.22889 7.06727 1.22889 7.18466 1.17555L8.62387 0.521619C9.25544 0.234653 9.99299 0.478615 10.3349 1.06532ZM9.16381 4.70595L5.97816 8.34669L4.81571 7.18424C4.6413 7.00983 4.35853 7.00983 4.18412 7.18424C4.00971 7.35865 4.00971 7.64142 4.18412 7.81583L5.68412 9.31583C5.86679 9.49851 6.16589 9.48855 6.33602 9.29412L9.83602 5.29412C9.99844 5.1085 9.97963 4.82635 9.794 4.66393C9.60838 4.50151 9.32623 4.52032 9.16381 4.70595Z" fill="url(#paint0_linear_3192_450)" />
                            <defs>
                                <linearGradient id="paint0_linear_3192_450" x1="-7.49927" y1="17.4999" x2="22.0007" y2="-3.00012" gradientUnits="userSpaceOnUse">
                                    <stop offset="0.307292" stopColor="#3928CC" />
                                    <stop offset="0.307392" stopColor="#6517CC" />
                                    <stop offset="0.53125" stopColor="#2D57C2" />
                                    <stop offset="0.947917" stopColor="#479FAF" />
                                </linearGradient>
                            </defs>
                        </svg>
                    }}>{t('edge_featured')}</Badge></Link></Tooltip>
            : null;

    const UsersCountBadge =
        result['activeInstallCount']
            ? <Badge shape="rounded" color="subtle">{result['activeInstallCount']}{" "}{t('users_count')}</Badge>
            : null;

    const EdgeAddonBadge =
        result['productType'] && result['productType'] === "BrowserAddOn"
            ? <Tooltip
                content={t('edge_addon_tooltip')}
                relationship="description"><Badge shape="rounded" color="informative" icon={<img src="https://edgestatic.azureedge.net/welcome/static/favicon.png" height={"16px"} alt="Microsoft Edge Icon" />}>{t('edge_addon_badge')}</Badge></Tooltip>
            : null;

    return (
        <>
            <section className={styles.section}><Card className={styles.card}>
                <CardHeader
                    image={
                        <img
                            src={getResultValue('iconUrl')}
                            width="32px" height="32px" alt="App Icon"
                        />
                    }
                    header={
                        <Body1>
                            <b>{getResultValue('title')}</b> {OSTypBadge} <Badge shape="rounded" color="brand" icon={<PersonCircleFilled />}>{getResultValue('publisherName')}{publisherId}</Badge>
                        </Body1>
                    }
                    description={<div><Body1>{category}</Body1>{UsersCountBadge}</div>}
                />
                <div style={{ display: 'flex', gap: '8px' }}>
                    {edgeAddonFeaturedBadge}<RatingDisplay value={getResultValue('averageRating')} count={getResultValue('ratingCount')} />
                </div>
                {releaseDateBadge}
                {verifiedMicrosoftProductBadge}
                {EdgeAddonBadge}
                <div style={{ display: 'flex', gap: '8px' }}>
                    {approximateSizeBadge}
                    {priceBadge}
                    {isPrivatePreviewBadge}
                    {isPwaBadge}
                </div>
                {packageFamilyName}
                {purchaseEnabled}
                <CardPreview>
                    {carousel}
                </CardPreview>
                <Badge shape="rounded" color="informative" icon={<Clock20Regular />}><Text>{t('configuration_last_updated_at')} {lastUpdated} {t('config_from')} {getResultValue('catalogSource')}</Text></Badge>
                {selfHosted}
                {installer}
                <CardFooter>
                    {viewInStoreButton}
                    {downloadButton}
                </CardFooter>
            </Card>
            </section>

            <section className={styles.section}>
                <Card>
                    <CardHeader
                        header={
                            <Body1>
                                {t('description')}
                            </Body1>
                        } />
                    <CardFooter>
                        <Text>
                            {description}
                        </Text>
                    </CardFooter>
                </Card>
            </section>

            {productAgeRatings}

            {releaseNotesCard}

            {supportedLanguages}

            {platforms}

            {permissionsRequiredCard}

            {videoInstances}

            {downloadabilityCard}

            {warningMsgs}

            {privacyUrl}
        </>
    )
}

export default DisplayItem;
